if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.searchContent = this.querySelector('.search__content');
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.isHeaderSearch = this.closest('header') !== null;

        this.states = {
          OPEN: 'predictive-search-open',
          LOADING: 'btn--loading',
          SEARCH_OPEN: 'search-open'
        };
      }

      connectedCallback() {
        // The header and the search page re-render in the theme editor: the document listeners must go with them.
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.restoreCategorySearch();
        this.resetButton.addEventListener('click', this.clear.bind(this), { signal });
        this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300), { signal });
        this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
        document.addEventListener('click', this.handleClickOutside.bind(this), { signal });
        this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });

        document.addEventListener(
          'menu-drawer:open',
          () => {
            this.classList.remove('predictive-search-open');
            document.body.classList.remove('search-open');
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get searchProductTypes() {
        return this.querySelector('.search__types select');
      }

      get form() {
        return this.querySelector('form');
      }

      get headerCollapseOnScroll() {
        return this.header.dataset.collapseOnScroll === 'true';
      }

      onFocus(event) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
        }

        if (!this.searchRecommendationEmpty) {
          this.searchContent.classList.remove('hidden');
        }
        this.classList.add('predictive-search-open');
        if (this.getQuery().length === 0) {
          if (this.searchRecommendationEmpty) {
            this.searchContent.classList.add('hidden');
          }
          return;
        }
        const url = this.setupURL().toString();
        // Reopening shows the results of a query the buyer already searched: not a search update.
        this.renderSection(url, { isReopen: true });
      }

      getQuery() {
        return this.input.value.trim();
      }

      clear(event = null) {
        event?.preventDefault();
        this.latestSearchURL = null;
        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.toggleSearchState(false);
      }

      handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
          const url = this.setupURL().toString();
          this.renderSection(url);
        }
      }

      categoryTypes(category) {
        const types = JSON.parse(this.dataset.productTypes || '[]');
        return types.filter((type) => {
          const value = type.toLowerCase();
          if (category === 'chandeliers') return value === 'lighting > ceiling lights > chandeliers' || value === 'lighting > ceiling lights > staircase chandeliers';
          if (category === 'pendants') return ['pendant light', 'pendant lighting', 'lighting > ceiling lights > pendant lighting'].includes(value);
          if (category === 'ceiling_lights') return ['ceiling lamp', 'ceiling light', 'track light'].includes(value) || value.startsWith('lighting > ceiling fans >') || (value.startsWith('lighting > ceiling lights >') && !/chandeliers|pendant lighting/.test(value));
          if (category === 'wall_lights') return value.startsWith('lighting > wall lights >');
          if (category === 'lamps') return value.startsWith('lighting > table & floor lamps >');
          if (category === 'outdoor_lighting') return value.startsWith('lighting > outdoor lighting >') || ['lighting > string lights > outdoor string lights', 'lighting > string lights > solar string lights'].includes(value);
          return false;
        });
      }

      categoryClause(category = this.searchProductTypes?.value) {
        if (!category) return '';
        const types = this.categoryTypes(category);
        const quoted = (value) => '"' + value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
        return '(' + (types.length ? types : ['__lexoka_no_matching_category__']).map((type) => `product_type:${quoted(type)}`).join(' OR ') + ')';
      }

      scopedQuery() {
        const query = this.getQuery();
        const clause = this.categoryClause();
        if (!clause) return query;
        const safeQuery = query.replace(/[\\:()"']/g, '\\$&').replace(/\b(AND|OR|NOT)\b/g, '"$1"');
        return query ? `${clause} AND (${safeQuery})` : clause;
      }

      restoreCategorySearch() {
        if (!this.searchProductTypes) return;
        const params = new URLSearchParams(window.location.search);
        const scoped = params.get('q') || '';
        for (const option of this.searchProductTypes.options) {
          if (!option.value) continue;
          const clause = this.categoryClause(option.value);
          if (scoped === clause || scoped.startsWith(clause + ' AND (')) {
            this.searchProductTypes.value = option.value;
            const raw = params.get('ll_q');
            this.input.value = raw !== null ? raw : (scoped === clause ? '' : scoped.slice(clause.length + 6, -1).replace(/\\([\\:()"'])/g, '$1').replace(/"(AND|OR|NOT)"/g, '$1'));
            return;
          }
        }
      }

      searchURL() {
        const url = new URL(this.form.getAttribute('action'), window.location.origin);
        url.searchParams.set('q', this.scopedQuery());
        url.searchParams.set('options[prefix]', 'last');
        if (this.searchProductTypes?.value) {
          url.searchParams.set('type', 'product');
          url.searchParams.set('ll_q', this.getQuery());
        }
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url;
      }

      onSubmit(event) {
        if (!this.searchProductTypes?.value) return;
        event.preventDefault();
        window.location.href = this.searchURL().toString();
      }

      setupURL() {
        const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
        url.searchParams.set('q', this.scopedQuery());
        if (this.searchProductTypes?.value) url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }
        const url = this.setupURL().toString();
        this.renderSection(url);
      }

      renderSection(url, { isReopen = false } = {}) {
        this.latestSearchURL = url;
        const searchUpdate = isReopen
          ? null
          : FoxTheme.storefrontEvents?.start(this, 'SearchUpdateEvent', {
              search: { query: this.getQuery() }
            });

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, searchUpdate)
          : this.renderSectionFromFetch(url, searchUpdate);
      }

      renderSectionFromCache(url, searchUpdate) {
        const responseText = this.cachedMap.get(url);
        (this.renderSearchResults(responseText), this.setAttribute('results', ''));
        searchUpdate?.resolve({ totalCount: this.getTotalResults() });
      }

      renderSectionFromFetch(url, searchUpdate) {
        this.setLoadingState(true);

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then((responseText) => {
            if (url !== this.latestSearchURL) return;
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);
            searchUpdate?.resolve({ totalCount: this.getTotalResults() });
          })
          .catch((error) => {
            searchUpdate?.reject(error);
            console.error('Error fetching data: ', error);
            this.setAttribute('error', 'Failed to load data');
          })
          .finally(() => this.setLoadingState(false));
      }

      getTotalResults() {
        const results = document.getElementById(`PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`);
        return Number(results?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      }
      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const targetElement = document.getElementById(id);

        if (targetElement) {
          const parser = new DOMParser();
          const parsedDoc = parser.parseFromString(responseText, 'text/html');
          const contentElement = parsedDoc.getElementById(id);

          if (contentElement) {
            this.searchContent?.classList.remove('hidden');
            targetElement.innerHTML = contentElement.innerHTML;
            targetElement.querySelectorAll('[data-search-query-label]').forEach((label) => {
              label.textContent = label.dataset.template.replace('__QUERY__', this.getQuery());
            });
            this.input.setAttribute('aria-expanded', 'true');

            if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
              document.body.classList.add('search-open');
              this.toggleNavButtonVisibility(true);
              this.classList.add('predictive-search-open');
            } else {
              this.classList.add('predictive-search-open');
            }
          } else {
            console.error(`Element with id '${id}' not found in the parsed response.`);
          }
        } else {
          console.error(`Element with id '${id}' not found in the document.`);
        }
      }

      handleClickOutside(event) {
        const target = event.target;
        const shouldClose = this.isHeaderSearch
          ? !this.contains(target) &&
            ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
              target.classList.contains('header__search-close'))
          : !this.contains(target);

        if (shouldClose) {
          setTimeout(() => this.toggleSearchState(false));
        }
      }

      toggleSearchState(isOpen) {
        this.input.setAttribute('aria-expanded', String(isOpen));
        this.classList.toggle(this.states.OPEN, isOpen);
        if (this.isHeaderSearch) {
          document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
          this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen && this.searchRecommendationEmpty) {
          this.searchContent?.classList.add('hidden');
        }
      }

      toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
          this.headerCollapseOnScroll &&
          this.toggleNavigationButton.classList.toggle('is-show', isOpen);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.setAttribute('loading', 'true');
          this.resetButton.classList.add(this.states.LOADING);
        } else {
          this.removeAttribute('loading');
          this.resetButton.classList.remove(this.states.LOADING);
          this.setAttribute('results', 'true');
        }
      }
    }
  );
}

